import type { Doc } from "@convex/_generated/dataModel";
import { cn } from "@plasma/utils/cn";
import { formatDistanceToNow } from "@plasma/utils/time";
import { useMutation, useQuery } from "convex/react";
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  Check,
  CheckCheck,
  Info,
  Trash2,
} from "lucide-react";

import {
  notificationsDeleteNotification,
  notificationsGetNotifications,
  notificationsGetUnreadCount,
  notificationsMarkAllAsRead,
  notificationsMarkAsRead,
} from "~/api-refs";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { useSidebar } from "./ui/sidebar";

type Notification = Doc<"notifications">;

function getSeverityIcon(severity: string) {
  switch (severity) {
    case "critical":
      return <AlertCircle className="text-destructive size-4" />;
    case "warning":
      return <AlertTriangle className="text-warning size-4" />;
    default:
      return <Info className="text-info size-4" />;
  }
}

function getSeverityBadgeVariant(
  severity: string
): "destructive" | "warning" | "information" | "secondary" {
  switch (severity) {
    case "critical":
      return "destructive";
    case "warning":
      return "warning";
    case "info":
      return "information";
    default:
      return "secondary";
  }
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: Notification["_id"]) => void;
  onDelete: (id: Notification["_id"]) => void;
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
}: NotificationItemProps) {
  const timeAgo = formatDistanceToNow(notification.createdAt);

  return (
    <div
      className={cn(
        "border-border flex gap-3 border-b p-3 transition-colors last:border-b-0",
        !notification.read && "bg-muted/50"
      )}
    >
      <div className="mt-0.5 flex-shrink-0">
        {getSeverityIcon(notification.severity)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h4
            className={cn(
              "text-sm font-medium",
              !notification.read && "font-semibold"
            )}
          >
            {notification.title}
          </h4>
          <Badge
            className="px-1.5 py-0 text-xs"
            variant={getSeverityBadgeVariant(notification.severity)}
          >
            {notification.severity}
          </Badge>
        </div>
        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
          {notification.message}
        </p>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-muted-foreground text-xs">{timeAgo}</span>
          <div className="flex gap-1">
            {!notification.read && (
              <Button
                className="h-6 px-2 text-xs"
                onClick={() => onMarkAsRead(notification._id)}
                size="sm"
                variant="ghost"
              >
                <Check className="mr-1 size-3" />
                Mark read
              </Button>
            )}
            <Button
              aria-label="Delete notification"
              className="text-muted-foreground hover:text-destructive h-6 w-6 p-0"
              onClick={() => onDelete(notification._id)}
              size="sm"
              variant="ghost"
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const notifications = useQuery(notificationsGetNotifications, {
    limit: 20,
  });
  const unreadCount = useQuery(notificationsGetUnreadCount, {});
  const markAsRead = useMutation(notificationsMarkAsRead);
  const markAllAsRead = useMutation(notificationsMarkAllAsRead);
  const deleteNotification = useMutation(notificationsDeleteNotification);

  const handleMarkAsRead = (notificationId: Notification["_id"]) => {
    void markAsRead({ notificationId });
  };

  const handleMarkAllAsRead = () => {
    void markAllAsRead({});
  };

  const handleDelete = (notificationId: Notification["_id"]) => {
    void deleteNotification({ notificationId });
  };

  const hasUnread = (unreadCount ?? 0) > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ""}`}
          className={cn(
            "border-input bg-background text-foreground hover:bg-muted relative inline-flex items-center justify-center rounded-lg border transition-colors",
            isCollapsed ? "h-8 w-8" : "h-8 gap-2 px-3"
          )}
          type="button"
        >
          <Bell className="size-4" />
          {!isCollapsed && <span className="text-sm">Notifications</span>}
          {hasUnread && (
            <span
              className={cn(
                "bg-destructive text-destructive-foreground absolute flex items-center justify-center rounded-full text-[10px] font-medium",
                isCollapsed
                  ? "top-0 right-0 h-4 w-4 translate-x-1/2 -translate-y-1/2"
                  : "h-5 min-w-5 px-1"
              )}
            >
              {unreadCount && unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 p-0"
        side={isCollapsed ? "right" : "top"}
        sideOffset={8}
      >
        <div className="border-border flex items-center justify-between border-b p-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {hasUnread && (
            <Button
              className="h-7 text-xs"
              onClick={handleMarkAllAsRead}
              size="sm"
              variant="ghost"
            >
              <CheckCheck className="mr-1 size-3" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[min(400px,60vh)]">
          {(() => {
            if (notifications === undefined) {
              return (
                <div className="space-y-3 p-4">
                  <div className="bg-muted h-4 w-3/4 animate-pulse rounded" />
                  <div className="bg-muted h-4 w-1/2 animate-pulse rounded" />
                </div>
              );
            }
            if (notifications.length === 0) {
              return (
                <div className="text-muted-foreground flex h-32 flex-col items-center justify-center">
                  <Bell className="mb-2 size-8 opacity-50" />
                  <p className="text-sm">No notifications</p>
                </div>
              );
            }
            return (
              <div>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    onDelete={handleDelete}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))}
              </div>
            );
          })()}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
